import React, { useState, useRef, useEffect, useCallback } from 'react';
import { GiHamburgerMenu } from 'react-icons/gi';
import arrow from '../Images/arrow_down.svg';
import arrow_up from '../Images/arrow_up.svg';
import darkLogo from '../Images/darkLogo.png';
import lightLogo from '../Images/lightLogo.png';
import leftArrow from '../Images/arrow-left.svg';
import Avatar from './Avatar.tsx';
import ThemeToggle from './ThemeToggle.tsx';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import useOnClickOutside from '../hooks/useOnClickOutside.ts';
import useAuth from '../hooks/useAuth.ts';
import useTheme from '../hooks/useTheme.ts';
import axios from 'axios';
import styles from './Header.module.css';
import { Modal, Button } from 'react-bootstrap';
import { jwtDecode } from 'jwt-decode'; // Updated import

const backendUrl = process.env.REACT_APP_BACKEND_URL;
const frontendUrl = process.env.REACT_APP_FRONTEND_URL;

type DecodedToken = {
  exp: number; // expiration time in seconds since epoch
  [key: string]: any;
};

function Header({
  title,
  details,
  back,
  setShowSidebar,
  showSidebar,
}: {
  title: string;
  details?: boolean;
  back?: boolean;
  setShowSidebar?: (value: boolean) => void;
  showSidebar?: boolean;
}) {
  const [showPopOut, setShowPopOut] = useState(false);
  const popUpMenuRef = useRef<HTMLDivElement | null>(null);
  const { name, email, setAuthenticated } = useAuth();
  const { theme } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();

  const [showRefreshModal, setShowRefreshModal] = useState(false);

  // Navigation items matching Machina landing page
  const navItems = [
  { path: '/landing', label: '🏠 Home' },
  { path: '/plan', label: '📋 Plan' },
  { path: '/completions', label: '🛠️ Design' },
  // { path: '/landing?screen=dashboard', label: '📈 Analyze' },
  { path: '/pad-data', label: '🤖 Closed Loop' },
];

  // Select logo based on current theme
  const logo = theme === 'dark' ? darkLogo : lightLogo;

  const handleLogOutClick = useCallback(async () => {
    // Clear local storage
    localStorage.clear();
    setAuthenticated(false);
    
    // Clear axios headers
    delete axios.defaults.headers.common['Authorization'];
    
    // Just redirect directly to sign-in page
    // The JWT will expire and Easy Auth session will timeout
    window.location.href = `${frontendUrl}/sign-in`;
  }, [setAuthenticated]);

  async function handleRefreshToken() {
    const token = localStorage.getItem('token');
    if (!token) {
      handleLogOutClick();
      return;
    }

    try {
      // Your existing refresh token endpoint should still work
      // It refreshes YOUR JWT token (not the Microsoft token)
      const response = await axios.post(
        `${backendUrl}/refresh-token`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const newToken = response.data.jwt;
      const decoded: DecodedToken = jwtDecode(newToken);
      const exp = decoded.exp;

      localStorage.setItem('token', newToken);
      localStorage.setItem('tokenExp', exp.toString());
      setShowRefreshModal(false);
    } catch (error) {
      console.log('Error refreshing token:', error);
      // If refresh fails, user needs to re-authenticate
      handleLogOutClick();
    }
  }

  const clickOutSideHandler = () => {
    setShowPopOut(false);
  };

  // @ts-ignore - Ignoring type issue with useOnClickOutside
  useOnClickOutside(popUpMenuRef, clickOutSideHandler);

  useEffect(() => {
    const checkTokenExpiration = () => {
      const token = localStorage.getItem('token');
      const tokenExp = localStorage.getItem('tokenExp');

      if (!token || !tokenExp) {
        // No token found, user is not logged in
        return;
      }

      const exp = parseInt(tokenExp, 10);
      const currentTime = Math.floor(Date.now() / 1000);

      if (exp < currentTime) {
        // Token expired
        console.log('logging out')
        handleLogOutClick();
      } else {
        // Token is valid, check how close it is to expiration
        const timeLeft = exp - currentTime;
        // If less than 300 seconds (5 minutes) until expiration, prompt refresh
        if (timeLeft < 300 && timeLeft > 0) {
          setShowRefreshModal(true);
        }
      }
    };

    // Check immediately on mount
    checkTokenExpiration();

    // Check every minute
    const interval = setInterval(checkTokenExpiration, 60 * 1000);
    return () => clearInterval(interval);
  }, [handleLogOutClick]); // Added handleLogOutClick to dependency array

  return (
    <header className={styles.headingContainer}>
      <div className={styles.headerLeft}>
        <div className={styles.logo}>
          <div className={styles.logo_controls}>
            {setShowSidebar ? (
              <button onClick={() => setShowSidebar(!showSidebar)} className={styles.hamburger_menu}>
                <GiHamburgerMenu className={styles.hamburger_icon} />
                <img src={logo} alt="logo" className={styles.logo_on_button} />
              </button>
            ) : (
              <div className={styles.hamburger_menu} style={{ cursor: 'default' }}>
                <img src={logo} alt="logo" className={styles.logo_on_button} />
              </div>
            )}
          </div>
        </div>
        
        {back && (
          <div className={styles.title_details}>
            <button className={styles.back_button} onClick={() => navigate(-1)}>
              <img src={leftArrow} alt="leftArrow" style={{ marginRight: '8px' }} />
            </button>
            <h1 className={styles.heading}>{title}</h1>
          </div>
        )}
        {!back && (
          <div className={styles.titleCompact}>
            <h1 className={styles.heading}>{title}</h1>
          </div>
        )}
      </div>

      {/* Navigation buttons - centered */}
      <nav className={styles.nav}>
        {navItems.map((item) => {
          // Check if this nav item is active by comparing pathname and search params
          const isActive = item.path.includes('?')
            ? location.pathname + location.search === item.path
            : location.pathname === item.path && !location.search;
          
          return (
            <button
              key={item.path}
              className={`${styles.navBtn} ${isActive ? styles.navBtnActive : ''}`}
              onClick={() => navigate(item.path)}
            >
              {item.label}
            </button>
          );
        })}
      </nav>

      <div className={styles.header_right}>
        <div
          className={styles.user_menu}
          onClick={() => setShowPopOut(!showPopOut)}
          ref={popUpMenuRef}
        >
          <Avatar name={name || 'User'} size="medium" className={styles.user_img} />
          <div className={styles.user_info}>
            <p className={styles.user_name}>{name || 'User'}</p>
            <p className={styles.user_email}>{email || 'No email'}</p>
          </div>
          <ThemeToggle className={styles.theme_toggle_in_user} />
          <img src={showPopOut ? arrow_up : arrow} alt="arrow" />
          {showPopOut && (
            <div className={styles.popOut}>
              <ul className={styles.popOutList}>
                <NavLink to={'/profile'} className={styles.link}>
                  <li className={styles.popOutItem}>User Profile</li>
                </NavLink>
                <li className={styles.popOutItem} onClick={handleLogOutClick}>
                  Log out
                </li>
              </ul>
            </div>
          )}
        </div>
      </div>

      {/* Modal to prompt token refresh */}
      <Modal show={showRefreshModal} onHide={() => setShowRefreshModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Session Expiring Soon</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Your session is about to expire. Would you like to refresh your token to stay logged in?
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleLogOutClick}>
            Log Out
          </Button>
          <Button variant="primary" onClick={handleRefreshToken}>
            Refresh Token
          </Button>
        </Modal.Footer>
      </Modal>
    </header>
  );
}

export default Header;
